import {
  deleteSavedSearch,
  fetchHouseIdentity,
  fetchLotPhotos,
  fetchMyWatchedLotIds,
  fetchSavedSearches,
  houseLabel,
  matchesQuery,
  NO_FILTERS,
  SAVED_SEARCH_ALERT_PROMISE,
  saveSearch,
  searchLots,
  setSearchAlerts,
  setWatched,
  validateSavedSearch,
  type SavedSearch,
} from '@auctisync/api-client';
import type { Tables } from '@auctisync/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { EmptyState, LotCard, type LotCardLot } from '../../components/bidder-web';
import { useBidderHouses } from '../../hooks/useBidderHouses';
import { useConnection } from '../../hooks/useConnection';
import { useServerClock } from '../../hooks/useServerClock';
import { useSession } from '../../hooks/useSession';
import { supabase } from '../../lib/supabase';

type SearchLot = LotCardLot & {
  description: string | null;
  category_id: string | null;
  auction_id: string;
};

const LOT_COLUMNS =
  'id, lot_number, title, description, current_bid_cents, starting_bid_cents, bid_count, ends_at, has_reserve, category_id, auction_id';

/** Same ceiling and same reason as Browse — see the note there. */
const MAX_LOTS = 500;

/**
 * Search — and the one thing on this site that works while the bidder is asleep.
 *
 * NEW PAGE (web design handoff, `web/web-search.jsx`). Searching used to mean
 * opening a sale and using the browser's own find-in-page.
 *
 * 🔴 THE MATCHING IS `matchesQuery` FROM THE SHARED CLIENT. Every word must
 * match, in any order, anywhere in the title or description — so "milwaukee
 * drill" finds "Drill, Milwaukee M18", which a substring match on the whole
 * phrase would not. That is the single most common way a search feels broken,
 * and it is fixed in one place for both surfaces.
 *
 * AN EMPTY RESULT OFFERS THE ALERT RATHER THAN DEAD-ENDING. Lots are catalogued
 * continuously through a week; "nothing matches" is a statement about right
 * now, and the useful next move is to be told when that changes.
 *
 * SAVING AND ALERTING ARE TWO DECISIONS. `saveSearch` defaults `alertsEnabled`
 * to false and this page keeps that: quietly signing someone up for
 * notifications because they saved a phrase is how an app earns a mute.
 */
export function PublicSearch() {
  const [params, setParams] = useSearchParams();
  const { session, userId } = useSession();
  const { nowServerMs } = useServerClock();
  const { state: connection } = useConnection();
  const { selected, active, discoverHouse } = useBidderHouses();

  const [query, setQuery] = useState(() => params.get('q') ?? '');
  const [lots, setLots] = useState<SearchLot[]>([]);
  const [slugs, setSlugs] = useState<ReadonlyMap<string, string>>(new Map());
  const [photos, setPhotos] = useState<ReadonlyMap<string, { url: string }[]>>(new Map());
  const [watchedIds, setWatchedIds] = useState<Set<string>>(new Set());
  const [saved, setSaved] = useState<SavedSearch[]>([]);
  const [savedError, setSavedError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  // The house a saved search belongs to. Saved searches are scoped per house —
  // a bidder registered with three does not want "milwaukee" from all three in
  // one list, and the notification needs a house to attribute itself to.
  const tenantId = selected?.tenant_id ?? active?.tenant_id ?? null;
  const houseName = active === null ? 'this platform' : houseLabel(active);

  // ── every open lot ────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      let saleQuery = supabase
        .from('auctions')
        .select('id, slug, tenant_id')
        .eq('is_visible', true)
        .in('status', ['active', 'preview'])
        .order('ends_at', { ascending: true, nullsFirst: false });
      if (selected) saleQuery = saleQuery.eq('tenant_id', selected.tenant_id);

      const { data: saleRows } = await saleQuery;
      if (cancelled) return;
      const sales = (saleRows ?? []) as Pick<Tables<'auctions'>, 'id' | 'slug' | 'tenant_id'>[];
      setSlugs(new Map(sales.map((s) => [s.id, s.slug])));

      const firstTenant = sales[0]?.tenant_id;
      if (
        !selected &&
        firstTenant !== undefined &&
        sales.every((s) => s.tenant_id === firstTenant)
      ) {
        void fetchHouseIdentity(supabase, firstTenant).then((house) => {
          if (!cancelled) discoverHouse(house);
        });
      }

      if (sales.length === 0) {
        setLots([]);
        setLoading(false);
        return;
      }

      const { data } = await supabase
        .from('lots')
        .select(LOT_COLUMNS)
        .in(
          'auction_id',
          sales.map((s) => s.id),
        )
        .eq('is_visible', true)
        .eq('status', 'active')
        .order('lot_number', { ascending: true })
        .limit(MAX_LOTS);
      if (cancelled) return;
      const rows = (data ?? []) as SearchLot[];
      setLots(rows);
      setPhotos(await fetchLotPhotos(supabase, rows));
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [selected, discoverHouse]);

  useEffect(() => {
    if (userId === null) {
      setWatchedIds(new Set());
      return;
    }
    let cancelled = false;
    void fetchMyWatchedLotIds(supabase, userId)
      .then((ids) => {
        if (!cancelled) setWatchedIds(new Set(ids));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const reloadSaved = useCallback(async () => {
    if (userId === null || tenantId === null) {
      setSaved([]);
      return;
    }
    const res = await fetchSavedSearches(supabase, tenantId);
    if (res.ok) {
      setSaved(res.searches);
      setSavedError(null);
    } else {
      setSavedError(res.message);
    }
  }, [userId, tenantId]);

  useEffect(() => {
    void reloadSaved();
  }, [reloadSaved]);

  const toggleWatch = useCallback(
    (lotId: string) => {
      if (userId === null) return;
      const next = !watchedIds.has(lotId);
      setWatchedIds((prev) => {
        const copy = new Set(prev);
        if (next) copy.add(lotId);
        else copy.delete(lotId);
        return copy;
      });
      void setWatched(supabase, userId, lotId, next);
    },
    [userId, watchedIds],
  );

  const trimmed = query.trim();
  const results = useMemo(() => {
    if (trimmed === '') return [];
    return searchLots(lots, trimmed, NO_FILTERS, nowServerMs());
  }, [lots, trimmed, nowServerMs]);

  // Matches per saved phrase, from the lots already in hand. `matchesQuery` is
  // the same rule the results use, so a count can never disagree with the list
  // that opens when the phrase is clicked.
  const savedHits = useMemo(() => {
    const out = new Map<string, number>();
    for (const s of saved) {
      out.set(s.id, lots.filter((l) => matchesQuery(l, s.query)).length);
    }
    return out;
  }, [saved, lots]);

  const validity = validateSavedSearch(query);
  const alreadySaved = validity.ok && saved.some((s) => s.query === validity.query);

  async function handleSave() {
    if (!session || tenantId === null || !validity.ok) return;
    setBusy(true);
    const res = await saveSearch(supabase, {
      userId: session.user.id,
      tenantId,
      raw: query,
    });
    setBusy(false);
    if (!res.ok) {
      setSavedError(res.message);
      return;
    }
    setSavedError(null);
    await reloadSaved();
  }

  function updateQuery(next: string) {
    setQuery(next);
    // The query lives in the URL so a search is a link — shareable, bookmarkable,
    // and reachable by the browser's back button.
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next.trim() === '') p.delete('q');
        else p.set('q', next);
        return p;
      },
      { replace: true },
    );
  }

  const guest = session === null;

  return (
    <div className="w-wrap">
      <div style={{ paddingTop: 40, maxWidth: 720 }}>
        <h1 className="w-h1">Search</h1>
        <p className="w-body muted" style={{ marginTop: 10 }}>
          Search every open lot at {houseName}. Save a search and we will tell you when something
          matching it is listed.
        </p>
      </div>

      <div style={{ maxWidth: 720, marginTop: 22 }}>
        <div className="ws-field">
          <span aria-hidden>⌕</span>
          <input
            value={query}
            onChange={(e) => updateQuery(e.target.value)}
            placeholder="Try a brand, a tool type, or a lot number"
            aria-label="Search lots"
            autoFocus
          />
          {query !== '' ? (
            <button
              type="button"
              className="w-ico"
              style={{ width: 34, height: 34 }}
              onClick={() => updateQuery('')}
              aria-label="Clear the search"
            >
              ✕
            </button>
          ) : null}
        </div>
        {trimmed === '' && saved.length > 0 ? (
          <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
            <span className="w-lbl" style={{ alignSelf: 'center' }}>
              Saved
            </span>
            {saved.map((s) => (
              <button
                key={s.id}
                type="button"
                className="w-chip"
                onClick={() => updateQuery(s.query)}
              >
                {s.query}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <div className="ws-grid">
        <div>
          {loading ? (
            <p className="muted">Loading the catalogue…</p>
          ) : trimmed === '' ? (
            <EmptyState
              title="Search the whole sale"
              body={`${String(lots.length)} lot${lots.length === 1 ? ' is' : 's are'} open. Search by brand, item type or lot number — or start from a category.`}
              action={
                <Link to="/browse" className="w-btn w-btn-out">
                  Browse categories
                </Link>
              }
            />
          ) : results.length > 0 ? (
            <>
              <div className="w-toolbar">
                <p className="muted" style={{ fontSize: 14 }}>
                  <b style={{ color: 'var(--text)' }}>{results.length}</b>{' '}
                  {results.length === 1 ? 'lot' : 'lots'} matching “{trimmed}”
                </p>
                <SaveButton
                  guest={guest}
                  disabled={!validity.ok || busy || tenantId === null}
                  saved={alreadySaved}
                  onSave={() => void handleSave()}
                />
              </div>
              <div className="w-grid w-grid-3">
                {results.map((lot) => (
                  <LotCard
                    key={lot.id}
                    lot={lot}
                    auctionSlug={slugs.get(lot.auction_id) ?? ''}
                    photoUrl={photos.get(lot.id)?.[0]?.url ?? null}
                    nowServerMs={nowServerMs}
                    offline={connection === 'offline'}
                    {...(session
                      ? { watched: watchedIds.has(lot.id), onToggleWatch: toggleWatch }
                      : {})}
                  />
                ))}
              </div>
              {alreadySaved ? (
                <p className="muted" style={{ fontSize: 13, marginTop: 16, lineHeight: 1.55 }}>
                  Saved. Turn alerts on in the panel beside this to be told when a NEW lot matching
                  “{trimmed}” is listed.
                </p>
              ) : null}
            </>
          ) : (
            <EmptyState
              title={`Nothing open matches “${trimmed}”`}
              body="Lots are catalogued continuously through the week. Save this search and we will tell you the moment something matching it goes up."
              action={
                <SaveButton
                  guest={guest}
                  disabled={!validity.ok || busy || tenantId === null}
                  saved={alreadySaved}
                  onSave={() => void handleSave()}
                />
              }
            />
          )}
        </div>

        <aside className="ws-saved">
          <div className="wl-panel">
            <div className="wl-ph">
              <span className="w-lbl">Saved searches</span>
            </div>
            {guest ? (
              <p
                className="muted"
                style={{ fontSize: 13, padding: 14, margin: 0, lineHeight: 1.55 }}
              >
                Sign in to save a search and be told when matching lots are listed.
              </p>
            ) : saved.length === 0 ? (
              <p
                className="muted"
                style={{ fontSize: 13, padding: 14, margin: 0, lineHeight: 1.55 }}
              >
                Nothing saved yet. Search for something and save it — we will watch for it while you
                are not here.
              </p>
            ) : (
              <div>
                {saved.map((s) => (
                  <div className="ws-row" key={s.id}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <button
                        type="button"
                        style={{ fontWeight: 600, fontSize: 14, textAlign: 'left' }}
                        onClick={() => updateQuery(s.query)}
                      >
                        {s.query}
                      </button>
                      <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                        {savedHits.get(s.id) ?? 0} open now
                      </div>
                    </div>
                    <button
                      type="button"
                      className="sw"
                      aria-pressed={s.alertsEnabled}
                      aria-label={`Alerts for ${s.query}`}
                      onClick={() => {
                        const next = !s.alertsEnabled;
                        setSaved((prev) =>
                          prev.map((row) =>
                            row.id === s.id ? { ...row, alertsEnabled: next } : row,
                          ),
                        );
                        void setSearchAlerts(supabase, { id: s.id, enabled: next });
                      }}
                    >
                      <span className="sw-k" />
                    </button>
                    <button
                      type="button"
                      className="muted"
                      aria-label={`Delete the saved search ${s.query}`}
                      style={{ fontSize: 15 }}
                      onClick={() => {
                        setSaved((prev) => prev.filter((row) => row.id !== s.id));
                        void deleteSavedSearch(supabase, s.id);
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <p
                  className="muted"
                  style={{ fontSize: 12, padding: '10px 14px 14px', margin: 0, lineHeight: 1.5 }}
                >
                  {SAVED_SEARCH_ALERT_PROMISE}
                </p>
              </div>
            )}
            {savedError !== null ? (
              <p
                className="muted"
                style={{ fontSize: 12.5, padding: '0 14px 14px', margin: 0 }}
                role="alert"
              >
                {savedError}
              </p>
            ) : null}
          </div>
        </aside>
      </div>
    </div>
  );
}

function SaveButton({
  guest,
  disabled,
  saved,
  onSave,
}: {
  guest: boolean;
  disabled: boolean;
  saved: boolean;
  onSave: () => void;
}) {
  if (guest) {
    return (
      <Link to="/signin" className="w-btn w-btn-out w-btn-sm">
        Sign in to save searches
      </Link>
    );
  }
  return (
    <button
      type="button"
      className="w-btn w-btn-out w-btn-sm"
      aria-pressed={saved}
      disabled={disabled || saved}
      onClick={onSave}
    >
      {saved ? '✓ Search saved' : 'Save this search'}
    </button>
  );
}
